import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCategories, getFilter, getFilteredEvents, isLoading } from './app/selectors';
import { AppActions } from './app/slice';
import { IEvent } from './app/types';
import GalleryCard from './components/GalleryCard';
import './GalleryPage.scss';

const GalleryPage: React.FC = () => {
  const dispatch = useDispatch();
  const categories = useSelector(getCategories);
  const loading = useSelector(isLoading);
  const filter = useSelector(getFilter);
  const filteredEvents = useSelector(getFilteredEvents);

  useEffect(() => {
    if (!loading) return;
    dispatch(AppActions.startApp());
  }, [dispatch, loading]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || undefined;
    dispatch(AppActions.filterChanged(value));
  };

  const newestEvents = [...filteredEvents].sort((a, b) => (a.id > b.id ? -1 : 1));

  // Group events by category
  const groupedEvents = newestEvents.reduce(
    (acc, event) => {
      event.categories.forEach((category) => {
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(event);
      });
      return acc;
    },
    {} as Record<string, IEvent[]>
  );

  // Add newest events at the top
  const finalGroupedEvents = { Newest: newestEvents, ...groupedEvents };

  return (
    <div className="gallery-page">
      <h1 className="gallery-page__title">Kostenlose CME-Fortbildungen on Demand</h1>
      <p className="gallery-page__subtitle">
        CME-Punkte sammeln mit praxisnahem Wissen. z. B. für Allgemeinmedizin und Neurologie
      </p>

      <div className="gallery-filter">
        <label htmlFor="category-select">Filter by category:</label>
        <select id="category-select" value={filter || ''} onChange={handleFilterChange}>
          <option value="">All</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="gallery-loading">Loading...</div>
      ) : (
        <div className="gallery-content">
          {Object.entries(finalGroupedEvents).map(([category, events]) => {
            const itemsToShow = category === 'Newest' ? 3 : 6;
            return (
              <div key={category} className="gallery-category">
                <h2 className="gallery-category__title">{category}</h2>
                <div className="gallery-category__grid">
                  {events.slice(0, itemsToShow).map((event) => (
                    <a
                      href="https://neuronation-med.de/cme/supporting-patients-with-mci"
                      target="_top"
                      aria-label={`View course: ${event.title}`}
                    >
                      <GalleryCard
                        key={event.id}
                        heading={event.title}
                        categories={event.categories}
                        authorName={event.presenterName}
                        authorTitle={event.presenterDescription}
                        authorImage={event.presenterImageURL}
                      />
                    </a>
                  ))}
                </div>
                {events.length > itemsToShow && <button className="gallery-category__show-more">Show More</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
